
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { NameSchema } from '@shared/user';
import {
  Button,
  Card,
  Heading,
  PageHeader,
  Row,
  Stack,
  Text,
} from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { useCurrentUser, useHasRole, SESSION_QUERY_KEY } from '@client/shared/auth/index.ts';
import { updateGroup } from './group-operations.ts';
import { BillingSection } from './BillingSection.tsx';
import { TransferOwnershipSection } from './TransferOwnershipSection.tsx';
import styles from './GroupSettings.module.css';

const GroupNameFormSchema = z.object({ name: NameSchema });

export function GroupSettings() {
  const session = useCurrentUser();
  // The name form is admin territory; billing is owner + billing-role territory. A user can
  // hold either without the other, so each card gates itself.
  const canEditName = useHasRole('admin');
  const canManageBilling = useHasRole('billing');
  const isOwner = useHasRole('owner');
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: updateGroup,
    // The group name also rides in the session (it labels the shell chip), so a rename must
    // refetch /auth/me or the chip stays stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
  });

  const form = useForm({
    defaultValues: { name: session?.group.name ?? '' },
    validators: { onSubmit: zodValidator(GroupNameFormSchema) },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      setSaved(false);
      try {
        const group = await mutation.mutateAsync({ name: value.name });
        form.reset({ name: group.name });
        setSaved(true);
      } catch (err) {
        setSubmitError(messageFor(err, 'Couldn’t save the group name. Try again.'));
      }
    },
  });

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 2600);
    return () => window.clearTimeout(t);
  }, [saved]);

  if (!session) return null;

  return (
    <Stack gap={6} className={styles.page}>
      <PageHeader
        eyebrow="Your organization"
        title="Group settings"
        description="Manage your group’s settings, plan, and ownership."
      />

      {canEditName && (
      <Card pad="lg" spine as="section" aria-labelledby="group-name-heading">
        <Stack gap={5}>
          <Stack gap={1}>
            <Heading level={3} as="h2" id="group-name-heading">
              Group name
            </Heading>
            <Text size="sm" tone="soft">
              Everyone in your group sees this name in the top bar.
            </Text>
          </Stack>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            noValidate
          >
            <Row gap={4} className={styles.nameRow}>
              <form.Field
                name="name"
                validators={{ onChange: zodValidator(NameSchema) }}
              >
                {(field) => (
                  <TextField
                    field={field}
                    fieldClassName={styles.nameField}
                    label="Group name"
                    autoComplete="organization"
                    onValueChange={() => setSaved(false)}
                  />
                )}
              </form.Field>

              <div className={styles.nameActions}>
                {saved && (
                  <span className={styles.savedFlag} role="status">
                    <CheckGlyph />
                    Saved
                  </span>
                )}
                <form.Subscribe selector={(s) => [s.isSubmitting, s.values.name, s.isDirty] as const}>
                  {([isSubmitting, name, isDirty]) => (
                    <Button
                      type="submit"
                      loading={Boolean(isSubmitting)}
                      disabled={!isDirty || (name as string).trim().length === 0}
                    >
                      Save name
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            </Row>

            {submitError && (
              <Text size="sm" tone="danger" role="alert">
                {submitError}
              </Text>
            )}
          </form>
        </Stack>
      </Card>
      )}

      {canManageBilling && <BillingSection />}

      {isOwner && <TransferOwnershipSection />}
    </Stack>
  );
}

function CheckGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5 10 17.5 19 6.5" />
    </svg>
  );
}
